import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { TenantProvisioningService, TrialSignupPayload } from '../../services/tenant-provisioning.service';
import { ConfigService } from '../../services/config.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule],
  template: `
  <div class="container py-5" style="max-width: 720px;">
    <h2 class="mb-3">Solicitar prueba del sistema</h2>
    <p class="text-muted mb-4">Completa tus datos para crear un entorno de prueba (tenant/realm) y acceder a la aplicación.</p>

    <form [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>
      <div class="mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Nombre completo</mat-label>
          <input matInput formControlName="fullName" placeholder="Tu nombre" />
        </mat-form-field>
        <div class="text-danger small" *ngIf="submitted && form.get('fullName')?.invalid">Ingresa tu nombre.</div>
      </div>

      <div class="mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Email</mat-label>
          <input matInput type="email" formControlName="email" placeholder="tu@email.com" />
        </mat-form-field>
        <div class="text-danger small" *ngIf="submitted && form.get('email')?.invalid">Ingresa un email válido.</div>
      </div>

      <div class="mb-3">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Whatsapp</mat-label>
          <input matInput formControlName="whatsapp" placeholder="+56 9 1234 5678" />
        </mat-form-field>
        <div class="text-danger small" *ngIf="submitted && form.get('whatsapp')?.invalid">Ingresa un Whatsapp válido.</div>
      </div>

      <div class="mb-4">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Nombre del Establecimiento</mat-label>
          <input matInput formControlName="businessName" placeholder="Mi Cafetería" />
        </mat-form-field>
        <div class="text-danger small" *ngIf="submitted && form.get('businessName')?.invalid">Ingresa el nombre de tu establecimiento.</div>
      </div>

      <button mat-raised-button color="primary" class="me-2" type="submit" [disabled]="loading">
        {{ loading ? 'Creando...' : 'Crear entorno de prueba' }}
      </button>
      <button mat-button type="button" (click)="goHome()" [disabled]="loading">Cancelar</button>
    </form>

    <div class="mt-4 text-muted small">
      Al continuar, se creará un tenant/realm en nuestro sistema IAM y recibirás instrucciones por correo.
    </div>
  </div>
  `,
})
export class SignupComponent {
  form: FormGroup;
  loading = false;
  submitted = false;

  constructor(
    private fb: FormBuilder,
    private provisioning: TenantProvisioningService,
    private config: ConfigService,
    private router: Router,
    private toastr: ToastrService
  ) {
    this.form = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      whatsapp: ['', [Validators.required, Validators.minLength(6)]],
      businessName: ['', [Validators.required, Validators.minLength(2)]],
    });
  }

  async onSubmit() {
    this.submitted = true;
    if (this.form.invalid || this.loading) return;
    this.loading = true;
    const payload: TrialSignupPayload = this.form.value;
    try {
      const res = await this.provisioning.provisionTrialTenant(payload).toPromise();
      if (!res?.keycloakUrl || !res?.realm || !res?.clientId) {
        this.toastr.error('No se pudo obtener la configuración de acceso.');
        this.loading = false;
        return;
      }
      this.config.setKeycloakOverrides(res.keycloakUrl, res.realm, res.clientId);
      this.toastr.success('Entorno creado. Redirigiendo a acceso...');
      setTimeout(() => {
        this.router.navigateByUrl('/admin').then(() => window.location.reload());
      }, 400);
    } catch (e) {
      console.error('Provision error', e);
      this.toastr.error('Hubo un problema creando el entorno. Intenta nuevamente.');
    } finally {
      this.loading = false;
    }
  }

  goHome() { this.router.navigateByUrl('/'); }
}
